import { formatTime, type Track } from './queue';
import { usePlayer } from './PlayerProvider';

/**
 * The persistent player bar.
 *
 * It renders whatever is current and never owns state, so it survives navigation with the queue.
 *
 * Three honesties are built into the layout rather than left to a caller:
 *
 * - **A module (MIDI, XM, MOD) has no scrubber.** The browser cannot decode it, so there is no
 *   position and no seek; the bar says what it is instead of offering a control that cannot work.
 * - **A failed item is visible.** It is marked, not silently skipped.
 * - **A derived title is labelled as derived.** Nothing here presents a generated name as if
 *   someone had chosen it.
 */

export interface PlayerBarProps {
  /** Called when the current item's detail should be shown. */
  onOpenDetail?: (track: Track) => void;
  className?: string;
}

export function PlayerBar({ onOpenDetail, className }: PlayerBarProps) {
  const { state, dispatch, track, canSeek, progress, durationMs } = usePlayer();

  if (!track) {
    return (
      <div className={join('player-bar player-bar--empty', className)} aria-live="polite">
        <span className="player-bar__idle">Pick something to listen to</span>
      </div>
    );
  }

  const failed = state.failed.includes(track.id);

  return (
    <div className={join('player-bar', className)} aria-live="polite">
      <div className="player-bar__meta">
        <button
          type="button"
          className="player-bar__title"
          onClick={() => onOpenDetail?.(track)}
          title={track.title}
        >
          {track.title}
        </button>
        <span className="player-bar__sub">
          {track.container ?? 'unknown format'}
          {track.blockHeight !== undefined && <> · block {track.blockHeight}</>}
          {track.titleSource === 'derived' && <span className="player-bar__derived"> · name derived</span>}
        </span>
      </div>

      <div className="player-bar__transport">
        <button type="button" onClick={() => dispatch({ type: 'previous' })} aria-label="Previous">
          ⏮
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: 'toggle' })}
          aria-label={state.playing ? 'Pause' : 'Play'}
          className="player-bar__play"
        >
          {state.playing ? '⏸' : '▶'}
        </button>
        <button type="button" onClick={() => dispatch({ type: 'next' })} aria-label="Next">
          ⏭
        </button>
      </div>

      <div className="player-bar__progress">
        {canSeek ? (
          <>
            <span className="player-bar__time">{formatTime(state.positionMs)}</span>
            <input
              type="range"
              min={0}
              max={durationMs}
              value={Math.min(state.positionMs, durationMs)}
              onChange={(e) => dispatch({ type: 'seek', positionMs: Number(e.target.value) })}
              aria-label="Seek"
              className="player-bar__scrub"
              style={{ ['--progress' as string]: `${progress * 100}%` }}
            />
            <span className="player-bar__time">{formatTime(durationMs)}</span>
          </>
        ) : (
          // No scrubber for something that cannot be scrubbed. Say what it is instead.
          <span className="player-bar__module">
            {track.kind === 'module' ? 'module — playback in the browser not supported yet' : 'no duration known'}
          </span>
        )}
      </div>

      <div className="player-bar__volume">
        <button type="button" onClick={() => dispatch({ type: 'toggleMute' })} aria-label="Mute">
          {state.muted ? '🔇' : '🔊'}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={state.muted ? 0 : state.volume}
          onChange={(e) => dispatch({ type: 'setVolume', volume: Number(e.target.value) })}
          aria-label="Volume"
        />
      </div>

      {failed && (
        <span className="player-bar__failed" role="status">
          this inscription could not be played
        </span>
      )}
    </div>
  );
}

function join(...parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function PlayerQueue({ limit = 50 }: { limit?: number }) {
  const { state, dispatch } = usePlayer();
  return (
    <ol className="player-queue">
      {state.queue.slice(0, limit).map((t, i) => (
        <li
          key={t.id}
          className={join(
            'player-queue__item',
            i === state.current ? 'player-queue__item--current' : undefined,
            state.failed.includes(t.id) ? 'player-queue__item--failed' : undefined,
          )}
        >
          <button type="button" onClick={() => dispatch({ type: 'play', index: i })}>
            {i === state.current && state.playing ? '⏸' : '▶'} {t.title}
          </button>
        </li>
      ))}
    </ol>
  );
}
